import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { dataDeletionApi, RequestError, type DataDeletionResponse } from '../api/client';
import {
  forgetMyRequestId,
  loadMyRequestIds,
  rememberMyRequestId,
} from '../utils/myDataRequests';

/** 保存領域が使えない環境（プライベートブラウズ等）では null */
function localStorageOrNull(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function formatDateTimeJst(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

/** EMP-S07 データ削除・匿名化（API 連携） */
export default function SettingsDataDeletion() {
  const { t } = useTranslation();
  const { getToken, user } = useAuth();
  const userId = user?.userId ?? '';
  /**
   * 削除の範囲。最初は選ばれていない状態にする（課題 #101。以前は「すべてのデータ」が最初から選ばれており、
   * 確かめずに押すと全データの削除を申請してしまった）
   */
  const [scope, setScope] = useState<'' | 'all' | 'last_30_days'>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastRequest, setLastRequest] = useState<DataDeletionResponse | null>(null);
  /**
   * この端末から出した申請（課題 #93）。取り消しはこの一覧からだけ行い、
   * 申請 ID を入力させる導線は作らない。
   */
  const [myRequests, setMyRequests] = useState<DataDeletionResponse[]>([]);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
  const [rowMessage, setRowMessage] = useState<Record<string, { ok: boolean; text: string }>>({});

  const statusText = useCallback(
    (status: string) => t(`settings.deletion.status.${status}`, { defaultValue: status }),
    [t]
  );

  /** 覚えている申請の状態を取り直す。見つからない申請は忘れる */
  const loadMyRequests = useCallback(() => {
    const token = getToken();
    const ids = loadMyRequestIds(localStorageOrNull(), userId);
    if (!token || ids.length === 0) return;
    Promise.all(
      ids.map((id) =>
        dataDeletionApi.get(id, token).catch((err) => {
          if (err instanceof RequestError && err.status === 404) {
            forgetMyRequestId(localStorageOrNull(), userId, id);
          }
          return null;
        })
      )
    ).then((rows) => setMyRequests(rows.filter((r): r is DataDeletionResponse => r != null)));
  }, [getToken, userId]);

  useEffect(() => {
    loadMyRequests();
  }, [loadMyRequests]);

  const handleDeletionRequest = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    if (!scope) {
      setSubmitError(t('settings.deletion.scopeRequired'));
      return;
    }
    // 押し間違いを防ぐため、範囲を示して確かめる（課題 #101）
    const scopeLabel = scope === 'last_30_days' ? t('settings.deletion.scopeLast30') : t('settings.deletion.scopeAll');
    if (!window.confirm(t('settings.deletion.confirm', { scope: scopeLabel }))) return;
    setSubmitError(null);
    setSubmitting(true);
    dataDeletionApi
      .create({ scope }, token)
      .then((res) => {
        setLastRequest(res);
        rememberMyRequestId(localStorageOrNull(), userId, res.id);
        setMyRequests((prev) => [res, ...prev.filter((r) => r.id !== res.id)]);
      })
      .catch((err) => {
        setSubmitError(err instanceof Error ? err.message : t('common.error'));
      })
      .finally(() => setSubmitting(false));
  };

  /** 本人による取り消し（課題 #93）。管理者の確認待ちの間だけ */
  const handleWithdraw = (request: DataDeletionResponse) => {
    const token = getToken();
    if (!token || withdrawingId) return;
    if (!window.confirm(t('settings.deletion.withdrawConfirm'))) return;
    setWithdrawingId(request.id);
    setRowMessage((prev) => {
      const next = { ...prev };
      delete next[request.id];
      return next;
    });
    dataDeletionApi
      .withdraw(request.id, token)
      .then((res) => {
        setMyRequests((prev) => prev.map((r) => (r.id === res.id ? { ...r, ...res } : r)));
        setRowMessage((prev) => ({
          ...prev,
          [request.id]: { ok: true, text: t('settings.deletion.withdrawDone') },
        }));
      })
      .catch((err) => {
        // 409: 管理者の対応が先に済んでいた。最新の状態を取り直して見せる
        const conflict = err instanceof RequestError && err.status === 409;
        setRowMessage((prev) => ({
          ...prev,
          [request.id]: {
            ok: false,
            text: conflict
              ? t('settings.deletion.withdrawConflict')
              : err instanceof Error
              ? err.message
              : t('common.error'),
          },
        }));
        if (conflict) loadMyRequests();
      })
      .finally(() => setWithdrawingId(null));
  };

  return (
    <div className="page settings-sub-page">
      <h1>{t('settings.deletion.title')}</h1>

      <form onSubmit={handleDeletionRequest} className="settings-card settings-form">
        <h3>{t('settings.deletion.deletionTitle')}</h3>
        <p className="settings-form-intro">{t('settings.deletion.deletionIntro')}</p>
        <div className="settings-form-group">
          <label htmlFor="deletion-scope">{t('settings.deletion.scope')}</label>
          <select
            id="deletion-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value as '' | 'all' | 'last_30_days')}
          >
            <option value="" disabled>
              {t('settings.deletion.scopePlaceholder')}
            </option>
            <option value="all">{t('settings.deletion.scopeAll')}</option>
            <option value="last_30_days">{t('settings.deletion.scopeLast30')}</option>
          </select>
        </div>
        {submitError && <p className="settings-form-error">{submitError}</p>}
        <button type="submit" className="settings-btn settings-btn-danger" style={{ maxWidth: '14rem' }} disabled={submitting}>
          {submitting ? t('common.loading') : t('settings.deletion.submit')}
        </button>
        {lastRequest && (
          <p className="settings-submitted">
            {t('settings.deletion.submitted', { id: lastRequest.id, status: statusText(lastRequest.status) })}
          </p>
        )}
        <p className="settings-deletion-approval-note">{t('settings.deletion.approvalFlowNote')}</p>
      </form>

      {myRequests.length > 0 && (
        <section className="settings-card" data-testid="my-deletion-requests">
          <h3>{t('settings.deletion.myRequestsTitle')}</h3>
          <p className="settings-note">{t('settings.deletion.myRequestsNote')}</p>
          <ul className="settings-request-list">
            {myRequests.map((r) => (
              <li key={r.id} className="settings-request-item" data-testid="my-deletion-request">
                <p className="settings-request-summary">
                  {t('settings.deletion.requestSummary', {
                    at: formatDateTimeJst(r.requested_at),
                    scope:
                      r.scope === 'last_30_days'
                        ? t('settings.deletion.scopeLast30')
                        : t('settings.deletion.scopeAll'),
                    status: statusText(r.status),
                  })}
                </p>
                <p className="settings-request-id">{t('settings.deletion.requestId', { id: r.id })}</p>
                {r.status === 'pending' && (
                  <button
                    type="button"
                    className="settings-btn"
                    onClick={() => handleWithdraw(r)}
                    disabled={withdrawingId != null}
                  >
                    {withdrawingId === r.id ? t('common.loading') : t('settings.deletion.withdraw')}
                  </button>
                )}
                {rowMessage[r.id] && (
                  <p
                    className={rowMessage[r.id].ok ? 'settings-submitted' : 'settings-form-error'}
                    role="status"
                  >
                    {rowMessage[r.id].text}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="settings-card">
        <h3>{t('settings.deletion.anonymizationTitle')}</h3>
        <p>{t('settings.deletion.anonymizationIntro')}</p>
        <p className="settings-note">{t('settings.deletion.anonymizationNote')}</p>
      </div>

      <p className="settings-back">
        <Link to="/dashboard/settings">{t('common.back')} → {t('settings.backToSettings')}</Link>
      </p>
    </div>
  );
}
